import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlignLeft,
  AtSign,
  Calendar,
  CheckSquare,
  Clock,
  DollarSign,
  FileText,
  FolderKanban,
  List,
  MessageCircle,
  Paperclip,
  Plus,
  Send,
  Tag,
  User as UserIcon,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Comment, Task, User } from '../../types';
import { tasksApi } from '../../services/api';
import { Button } from '../common/Button';
import { AttachmentPanel } from '../common/AttachmentPanel';
import { ParticipantSelector } from './ParticipantSelector';
import { formatBRLCurrency, formatBRLCurrencyInput, parseBRLCurrency } from '../../utils/currency';

interface TaskDetailPanelProps {
  projectId: string;
  projectName?: string;
  initialData?: Partial<Task>;
  members?: User[];
  defaultStatus?: Task['status'];
  onSubmit: (data: Partial<Task>) => Promise<void>;
  onCancel: () => void;
  onDelete?: (taskId: string) => void;
}

type DetailTab = 'updates' | 'checklist' | 'files';
type SelectedMember = User & { memberLabel: string };

const statusStyles: Record<Task['status'], { label: string; color: string }> = {
  todo: { label: 'Não iniciado', color: '#C24914' },
  in_progress: { label: 'Em andamento', color: '#FDAB3D' },
  in_review: { label: 'Parado', color: '#E2445C' },
  done: { label: 'Feito', color: '#00C875' },
};

const priorityLabels: Record<Task['priority'], string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
};

export function TaskDetailPanel({
  projectId,
  projectName,
  initialData,
  members = [],
  defaultStatus = 'todo',
  onSubmit,
  onCancel,
  onDelete,
}: TaskDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('updates');
  const [saving, setSaving] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [subtasks, setSubtasks] = useState<Task[]>([]);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [subtaskLoading, setSubtaskLoading] = useState(false);
  const [isMemberPickerOpen, setIsMemberPickerOpen] = useState(false);
  const priorityRef = useRef<HTMLSelectElement>(null);
  const startDateRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: defaultStatus,
    priority: 'medium' as Task['priority'],
    assignee_id: '',
    participant_ids: [] as string[],
    start_date: '',
    due_date: '',
    budget: '',
    project_id: projectId,
  });

  const taskId = initialData?.id;
  const selectedStatus = statusStyles[formData.status];
  const assignee = useMemo(
    () => members.find(member => member.id === formData.assignee_id),
    [members, formData.assignee_id]
  );
  const selectedMembers = useMemo(() => {
    const selected: SelectedMember[] = [];
    const usedIds = new Set<string>();

    if (assignee) {
      selected.push({ ...assignee, memberLabel: 'Responsável' });
      usedIds.add(assignee.id);
    }

    formData.participant_ids.forEach(participantId => {
      const participant = members.find(member => member.id === participantId);
      if (participant && !usedIds.has(participant.id)) {
        selected.push({ ...participant, memberLabel: 'Participante' });
        usedIds.add(participant.id);
      }
    });

    return selected;
  }, [assignee, formData.participant_ids, members]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      title: initialData?.title || '',
      description: initialData?.description || '',
      status: initialData?.status || defaultStatus,
      priority: initialData?.priority || 'medium',
      assignee_id: initialData?.assignee_id || '',
      participant_ids: initialData?.participants?.map(participant => participant.id) || initialData?.participant_ids || [],
      start_date: initialData?.start_date?.split('T')[0] || '',
      due_date: initialData?.due_date?.split('T')[0] || '',
      budget: formatBRLCurrency(initialData?.budget),
      project_id: projectId,
    }));
  }, [defaultStatus, initialData, projectId]);

  useEffect(() => {
    if (!taskId) {
      setComments([]);
      setSubtasks([]);
      return;
    }
    tasksApi.getComments(taskId)
      .then(res => setComments(res.data))
      .catch(() => setComments([]));
    tasksApi.getSubtasks(taskId)
      .then(res => setSubtasks(res.data))
      .catch(() => setSubtasks([]));
  }, [taskId]);

  const handleToggleSubtask = async (subtask: Task) => {
    const newStatus = subtask.status === 'done' ? 'todo' : 'done';
    setSubtasks(prev => prev.map(s => s.id === subtask.id ? { ...s, status: newStatus } : s));
    try {
      await tasksApi.update(subtask.id, { status: newStatus });
    } catch {
      setSubtasks(prev => prev.map(s => s.id === subtask.id ? { ...s, status: subtask.status } : s));
      toast.error('Falha ao atualizar item');
    }
  };

  const handleAddSubtask = async () => {
    if (!taskId || !newSubtaskTitle.trim()) return;
    setSubtaskLoading(true);
    try {
      const res = await tasksApi.create({
        title: newSubtaskTitle.trim(),
        project_id: projectId,
        parent_task_id: taskId,
        status: 'todo',
        priority: 'medium',
        position: subtasks.length,
      });
      setSubtasks(prev => [...prev, res.data]);
      setNewSubtaskTitle('');
      setAddingSubtask(false);
    } catch {
      toast.error('Falha ao criar item');
    } finally {
      setSubtaskLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        ...formData,
        assignee_id: formData.assignee_id || null,
        participant_ids: formData.participant_ids.filter(id => id !== formData.assignee_id),
        start_date: formData.start_date || undefined,
        due_date: formData.due_date || undefined,
        budget: parseBRLCurrency(formData.budget),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!taskId || !commentText.trim()) return;
    setCommentLoading(true);
    try {
      const response = await tasksApi.addComment(taskId, commentText.trim());
      setComments(prev => [...prev, response.data]);
      setCommentText('');
      toast.success('Atualização publicada');
    } catch {
      toast.error('Falha ao publicar atualização');
    } finally {
      setCommentLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[58%_42%] h-[82vh] max-h-[760px] overflow-hidden">
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
        <div className="flex-1 overflow-y-auto p-6 pb-4">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {taskId ? 'Detalhes da tarefa' : 'Criar tarefa'}
            </h2>
            <p className="text-sm text-gray-500 mt-2 flex items-center gap-1.5">
              <FolderKanban size={14} />
              em {projectName || initialData?.project_name || 'Projeto'}
            </p>
          </div>

          <div className="mb-6 flex flex-wrap gap-2">
            <TaskActionButton icon={<Plus size={14} />} onClick={() => setIsMemberPickerOpen(prev => !prev)}>
              Adicionar
            </TaskActionButton>
            <TaskActionButton icon={<Tag size={14} />} onClick={() => priorityRef.current?.focus()}>
              Etiquetas
            </TaskActionButton>
            <TaskActionButton icon={<Calendar size={14} />} onClick={() => startDateRef.current?.focus()}>
              Datas
            </TaskActionButton>
            <TaskActionButton icon={<CheckSquare size={14} />} onClick={() => setActiveTab('checklist')}>
              Checklist
            </TaskActionButton>
            <TaskActionButton icon={<Paperclip size={14} />} onClick={() => setActiveTab('files')}>
              Anexo
            </TaskActionButton>
          </div>

          <div className="mb-6">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-gray-500">
              <Users size={14} />
              Membros
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedMembers.map(member => (
                <MemberAvatar key={member.id} member={member} />
              ))}
              {selectedMembers.length === 0 && (
                <span className="text-sm text-gray-400">Nenhum membro adicionado</span>
              )}
              <button
                type="button"
                onClick={() => setIsMemberPickerOpen(prev => !prev)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200"
                aria-label="Adicionar membros"
                title="Adicionar membros"
              >
                <Plus size={16} />
              </button>
            </div>
            {isMemberPickerOpen && (
              <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                <label className="mb-1 block text-xs font-medium text-gray-500">Responsável</label>
                <select
                  value={formData.assignee_id}
                  onChange={event => setFormData(prev => ({
                    ...prev,
                    assignee_id: event.target.value,
                    participant_ids: prev.participant_ids.filter(id => id !== event.target.value),
                  }))}
                  className="mb-3 h-10 w-full rounded border border-gray-200 bg-gray-50 px-3 text-sm focus:border-primary-500 focus:bg-white focus:outline-none"
                >
                  <option value="">Sem responsável</option>
                  {members.map(member => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
                <label className="mb-1 block text-xs font-medium text-gray-500">Participantes</label>
                <ParticipantSelector
                  members={members}
                  selectedIds={formData.participant_ids}
                  assigneeId={formData.assignee_id}
                  onChange={participantIds => setFormData(prev => ({ ...prev, participant_ids: participantIds }))}
                />
              </div>
            )}
          </div>

          <div className="space-y-3">
            <PropertyRow icon={<List size={17} />} label="Grupo">
              <div className="h-10 bg-gray-50 rounded flex items-center px-3 text-sm text-gray-700">
                <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: selectedStatus.color }} />
                {selectedStatus.label}
              </div>
            </PropertyRow>

          <PropertyRow icon={<AlignLeft size={17} />} label="Nomear">
            <input
              value={formData.title}
              onChange={event => setFormData(prev => ({ ...prev, title: event.target.value }))}
              required
              className="w-full h-10 px-3 bg-gray-50 rounded border border-transparent focus:bg-white focus:border-primary-500 focus:outline-none text-sm"
              placeholder="Criar tarefa"
            />
          </PropertyRow>

          <PropertyRow icon={<UserIcon size={17} />} label="Responsável">
            <select
              value={formData.assignee_id}
              onChange={event => setFormData(prev => ({
                ...prev,
                assignee_id: event.target.value,
                participant_ids: prev.participant_ids.filter(id => id !== event.target.value),
              }))}
              className="w-full h-10 px-3 bg-gray-50 rounded border border-transparent focus:bg-white focus:border-primary-500 focus:outline-none text-sm"
            >
              <option value="">Sem responsável</option>
              {members.map(member => (
                <option key={member.id} value={member.id}>{member.name}</option>
              ))}
            </select>
          </PropertyRow>

          <PropertyRow icon={<List size={17} />} label="Status">
            <select
              value={formData.status}
              onChange={event => setFormData(prev => ({ ...prev, status: event.target.value as Task['status'] }))}
              className="w-full h-10 px-3 rounded border border-transparent focus:outline-none text-sm text-white font-medium"
              style={{ backgroundColor: selectedStatus.color }}
            >
              {Object.entries(statusStyles).map(([key, status]) => (
                <option key={key} value={key}>{status.label}</option>
              ))}
            </select>
          </PropertyRow>

          <PropertyRow icon={<DollarSign size={17} />} label="Orçamento">
            <input
              type="text"
              inputMode="numeric"
              value={formData.budget}
              onChange={event => setFormData(prev => ({ ...prev, budget: formatBRLCurrencyInput(event.target.value) }))}
              className="w-full h-10 px-3 bg-gray-50 rounded border border-transparent focus:bg-white focus:border-primary-500 focus:outline-none text-sm"
              placeholder="R$ 0,00"
            />
          </PropertyRow>

          <PropertyRow icon={<Calendar size={17} />} label="Cronograma">
            <div className="grid grid-cols-2 gap-2">
              <label className="min-w-0">
                <span className="mb-1 block text-xs text-gray-500">Início</span>
                <input
                  ref={startDateRef}
                  type="date"
                  value={formData.start_date}
                  onChange={event => setFormData(prev => ({ ...prev, start_date: event.target.value }))}
                  className="w-full h-10 px-3 bg-gray-50 rounded border border-transparent focus:bg-white focus:border-primary-500 focus:outline-none text-sm"
                />
              </label>
              <label className="min-w-0">
                <span className="mb-1 block text-xs text-gray-500">Prazo</span>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={event => setFormData(prev => ({ ...prev, due_date: event.target.value }))}
                  className="w-full h-10 px-3 bg-gray-50 rounded border border-transparent focus:bg-white focus:border-primary-500 focus:outline-none text-sm"
                />
              </label>
            </div>
          </PropertyRow>

          <PropertyRow icon={<FileText size={17} />} label="Prioridade">
            <select
              ref={priorityRef}
              value={formData.priority}
              onChange={event => setFormData(prev => ({ ...prev, priority: event.target.value as Task['priority'] }))}
              className="w-full h-10 px-3 bg-gray-50 rounded border border-transparent focus:bg-white focus:border-primary-500 focus:outline-none text-sm"
            >
              {Object.entries(priorityLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </PropertyRow>

          <PropertyRow icon={<Clock size={17} />} label="Última atualiza...">
            <div className="h-10 bg-gray-50 rounded flex items-center px-3 text-sm text-gray-500">
              {taskId ? 'Agora há pouco' : 'Será criada agora'}
            </div>
          </PropertyRow>
          </div>

          <textarea
            value={formData.description}
            onChange={event => setFormData(prev => ({ ...prev, description: event.target.value }))}
            className="w-full mt-5 px-3 py-2 bg-gray-50 rounded border border-transparent focus:bg-white focus:border-primary-500 focus:outline-none text-sm resize-none"
            rows={3}
            placeholder="Descrição da tarefa..."
          />

        </div>

        <div className="flex items-center gap-3 border-t border-gray-200 bg-white p-4">
          <Button type="submit" loading={saving} className="min-w-[140px] justify-center">
            {taskId ? 'Salvar tarefa' : 'Criar tarefa'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          {taskId && onDelete && (
            <Button type="button" variant="danger" onClick={() => onDelete(taskId)} className="ml-auto">
              Excluir
            </Button>
          )}
        </div>
      </form>

      <aside className="border-l border-gray-200 bg-white overflow-y-auto">
        <div className="px-5 pt-5 border-b border-gray-200">
          <div className="flex items-center gap-5 text-sm">
            <TabButton active={activeTab === 'updates'} onClick={() => setActiveTab('updates')} icon={<MessageCircle size={15} />}>
              Atualizações
            </TabButton>
            <TabButton active={activeTab === 'checklist'} onClick={() => setActiveTab('checklist')} icon={<CheckSquare size={15} />}>
              Checklist
            </TabButton>
            <TabButton active={activeTab === 'files'} onClick={() => setActiveTab('files')} icon={<Paperclip size={15} />}>
              Arquivos
            </TabButton>
          </div>
        </div>

        {activeTab === 'updates' && (
          <div className="p-5">
            <div className="border border-gray-200 rounded-lg p-3">
              <textarea
                value={commentText}
                disabled={!taskId}
                onChange={event => setCommentText(event.target.value)}
                className="w-full resize-none focus:outline-none text-sm disabled:bg-white disabled:text-gray-400"
                rows={3}
                placeholder={taskId ? 'Escreva uma atualização e mencione outros com @' : 'Crie a tarefa para publicar atualizações'}
              />
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-3 text-gray-400">
                  <AtSign size={16} />
                  <Paperclip size={16} />
                </div>
                <Button
                  type="button"
                  size="sm"
                  loading={commentLoading}
                  disabled={!taskId || !commentText.trim()}
                  icon={<Send size={14} />}
                  onClick={handleAddComment}
                >
                  Publicar
                </Button>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {comments.length === 0 ? (
                <div className="text-center py-14">
                  <MessageCircle size={34} className="text-primary-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-gray-900">Nenhuma atualização ainda</h3>
                  <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
                    Compartilhe o progresso, mencione um colega ou registre decisões importantes.
                  </p>
                </div>
              ) : comments.map(comment => (
                <div key={comment.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary-500 text-white text-xs font-semibold flex items-center justify-center">
                        {comment.user_name?.charAt(0).toUpperCase() || assignee?.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <span className="text-sm font-medium text-gray-800">{comment.user_name || 'Usuário'}</span>
                    </div>
                    <span className="text-xs text-gray-400">
                      {format(new Date(comment.created_at), "d MMM 'às' HH:mm", { locale: ptBR })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-line">{comment.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'checklist' && (
          <div className="p-5">
            {!taskId ? (
              <EmptySidePanel
                icon={<CheckSquare size={34} />}
                title="Checklist indisponível"
                text="Crie a tarefa para adicionar itens ao checklist."
              />
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <CheckSquare size={16} className="text-gray-500" />
                    {subtasks.filter(subtask => subtask.status === 'done').length}/{subtasks.length} concluídos
                  </div>
                  {!addingSubtask && (
                    <button
                      type="button"
                      onClick={() => setAddingSubtask(true)}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      + Adicionar item
                    </button>
                  )}
                </div>

                {subtasks.length > 0 && (
                  <div className="mb-4 h-1.5 w-full rounded-full bg-gray-100">
                    <div
                      className="h-1.5 rounded-full bg-green-500 transition-all duration-300"
                      style={{ width: `${(subtasks.filter(subtask => subtask.status === 'done').length / subtasks.length) * 100}%` }}
                    />
                  </div>
                )}

                {subtasks.length === 0 && !addingSubtask ? (
                  <EmptySidePanel
                    icon={<CheckSquare size={34} />}
                    title="Checklist vazio"
                    text="Adicione itens para acompanhar as etapas desta tarefa."
                  />
                ) : (
                  <div className="space-y-2">
                    {subtasks.map(subtask => (
                      <label key={subtask.id} className="flex cursor-pointer items-center gap-3 rounded border border-gray-100 p-3 hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={subtask.status === 'done'}
                          onChange={() => handleToggleSubtask(subtask)}
                          className="h-4 w-4 flex-shrink-0 cursor-pointer rounded accent-primary-500"
                        />
                        <span className={`text-sm ${subtask.status === 'done' ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                          {subtask.title}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {addingSubtask && (
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="text"
                      value={newSubtaskTitle}
                      onChange={event => setNewSubtaskTitle(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') { event.preventDefault(); handleAddSubtask(); }
                        if (event.key === 'Escape') { setAddingSubtask(false); setNewSubtaskTitle(''); }
                      }}
                      autoFocus
                      className="h-9 flex-1 rounded border border-gray-200 bg-gray-50 px-3 text-sm focus:border-primary-500 focus:outline-none"
                      placeholder="Nome do item..."
                    />
                    <Button
                      type="button"
                      size="sm"
                      loading={subtaskLoading}
                      disabled={!newSubtaskTitle.trim()}
                      onClick={handleAddSubtask}
                    >
                      Adicionar
                    </Button>
                    <button
                      type="button"
                      onClick={() => { setAddingSubtask(false); setNewSubtaskTitle(''); }}
                      className="px-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <AttachmentPanel
            ownerId={taskId}
            kind="task"
            disabledTitle="Arquivos indisponiveis"
            disabledText="Crie a tarefa para anexar arquivos."
            emptyTitle="Nenhum arquivo ainda"
            emptyText="Arquivos anexados a tarefa aparecerao aqui."
          />
        )}
      </aside>
    </div>
  );
}

function TaskActionButton({ icon, onClick, children }: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-8 items-center gap-1.5 rounded border border-gray-200 bg-gray-50 px-3 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100"
    >
      {icon}
      {children}
    </button>
  );
}

function MemberAvatar({ member }: { member: SelectedMember }) {
  return (
    <span
      className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white ring-2 ring-white"
      title={`${member.name} - ${member.memberLabel}`}
    >
      {getInitials(member.name)}
    </span>
  );
}

function getInitials(name?: string) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
}

function PropertyRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-4 items-center">
      <div className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
        <span className="text-gray-500">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      {children}
    </div>
  );
}

function TabButton({ active, onClick, icon, children }: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 pb-3 border-b-2 transition-colors ${
        active ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-600 hover:text-gray-900'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function EmptySidePanel({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="text-center py-20 px-6">
      <div className="text-primary-300 flex justify-center mb-4">{icon}</div>
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">{text}</p>
    </div>
  );
}
